import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { DropdownButton } from "PFComponents/dropdown_button";
import { Icon } from "PFComponents/icon";
import { useActivityEconomicsScenarioClone } from "PFCore/hooks/queries/activity/use_activity_economics_scenario_clone";
import { Activity, EconomicsScenario, Id } from "PFTypes";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { RemoveScenarioModal } from "../../../../../../parts/remove_scenario_modal";
import { ShareScenarioModal } from "../../../../../../parts/share_scenario_modal";
import { useEconomicsPermissions } from "../../../../../hooks/use_economics_permissions";

type ScenarioActionsProps = {
  activity: Activity;
  scenario: EconomicsScenario;
  onClone?: (scenarioId: Id) => void;
};

export const ScenarioActions = ({ activity, scenario, onClone }: ScenarioActionsProps) => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("activities", { keyPrefix: "show.economics" });

  const { canCreateScenarios, canManageScenario } = useEconomicsPermissions();

  const { mutate: cloneScenario } = useActivityEconomicsScenarioClone(activity.id);

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handleClone = useCallback(() => {
    cloneScenario(scenario.id, {
      onSuccess: (newScenario) => {
        showGrowl({
          kind: "success",
          message: t("scenarioCloneSuccess")
        });

        onClone?.(newScenario.id);
      },
      onError: () => {
        showGrowl({
          kind: "error",
          message: t("scenarioCloneError")
        });
      }
    });
  }, [cloneScenario, scenario.id, showGrowl, t, onClone]);

  const options = useMemo(
    () => [
      {
        id: "remove",
        global_id: "remove",
        displayElement: t("remove"),
        item: () => setIsRemoveModalOpen(true),
        disabled: !canManageScenario(scenario.creatorId)
      },
      {
        id: "share",
        global_id: "share",
        displayElement: t("share"),
        item: () => setIsShareModalOpen(true),
        disabled: !canManageScenario(scenario.creatorId)
      },
      {
        id: "clone",
        global_id: "clone",
        displayElement: t("clone"),
        item: () => handleClone(),
        disabled: !canCreateScenarios
      }
    ],
    [t, canManageScenario, scenario.creatorId, canCreateScenarios, handleClone]
  );

  return (
    <>
      <DropdownButton
        options={options}
        buttonKind="blank"
        buttonStyle={{ height: 18 }}
        style={{ marginLeft: "auto" }}
        handleChange={(fn) => fn && fn(scenario)}
      >
        <Icon name="menu-vertical" size="md" />
      </DropdownButton>
      {isRemoveModalOpen && (
        <RemoveScenarioModal
          scenarioId={scenario.id}
          activityId={activity.id}
          onClose={() => setIsRemoveModalOpen(false)}
        />
      )}
      {isShareModalOpen && (
        <ShareScenarioModal scenarioId={scenario.id} onClose={() => setIsShareModalOpen(false)} />
      )}
    </>
  );
};
